import { AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import FilePreview from "@rapid/components/file/Preview";
import { EmbeddedWorkflowPreview } from "./EmbeddedWorkflowPreview";
import { EmbeddedWorkflowPreviewMobile } from "./EmbeddedWorkflowPreviewMobile";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useCoreApp } from "@rapid/contexts/CoreAppContext";
import { BoundingBoxStrategyImpl } from "@rapid/utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@rapid/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@rapid/useTestWithMoreFiles";
import { useAddExampleData } from "@rapid/hooks/useAddExampleData";
import { IntegrationCodeSnippet } from "@rapid/components/flow/shared/Deploy/IntegrationCodeSnippet";
import { useDeployContext } from "@rapid/contexts/DeployContext";
import { useAuth } from "@rapid/contexts/AuthContext";
import { generateIntegrationCMS } from "@rapid/components/flow/shared/Deploy/documentation/generateIntegrationCMS";
import {
    ImageDeployment,
    VideoDeployment,
    DataMode
} from "@rapid/components/flow/shared/Deploy/documentation/types";
import { useMobile } from "@rapid/hooks/useMobile";
import { useCurrentMedia } from "@rapid/store";
import { VideoTrimmer } from "@rapid/components/file/VideoTrimmer";
import { OutOfCreditsModal } from "@rapid/components/flow/shared/OutOfCreditsModal";

// Share page always shows the test interface
export function ShareVisualAreaContent() {
    const [isHovered, setIsHovered] = useState(false);
    const { showLabels } = useCoreApp();
    const { testWithMoreFiles, uploadLimitError, isOutOfCredits, clearOutOfCreditsError } =
        useTestWithMoreFiles();
    const { mediaItemToTrim } = useMedia();
    const currentMedia = useCurrentMedia();
    const { workflowCredentials, dataMode } = useDeployContext();
    const { user } = useAuth();

    // Responsive breakpoint detection - using md breakpoint (768px)
    const isMobile = useMobile(768);

    // Generate copy for IMAGE and VIDEO data modes
    const copy = useMemo(() => {
        // Don't generate copy for PHONE or TEST_FILES mode
        if (dataMode === DataMode.PHONE || dataMode === DataMode.TEST_FILES) {
            return null;
        }

        const integrationCMS = generateIntegrationCMS(workflowCredentials);

        // Get the appropriate deployment type based on data mode
        const getDefaultDeploymentType = () => {
            if (dataMode === DataMode.IMAGE) {
                return ImageDeployment.SINGLE;
            } else if (dataMode === DataMode.VIDEO) {
                return VideoDeployment.FILE;
            }
            return ImageDeployment.SINGLE;
        };

        const selectedDeploymentType = getDefaultDeploymentType();
        return integrationCMS[dataMode][
            selectedDeploymentType as keyof (typeof integrationCMS)[typeof dataMode]
        ];
    }, [workflowCredentials, dataMode]);

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await testWithMoreFiles(files);
            onFinally?.();
        },
        [testWithMoreFiles]
    );

    const { addExampleDataFiles } = useAddExampleData({ stable: true });

    useEffect(() => {
        addExampleDataFiles();
    }, [addExampleDataFiles]);

    return (
        <AnimatePresence mode="wait">
            <div
                className="visualArea h-auto min-h-0"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="flex min-h-[80vh] flex-col gap-6">
                    <FilePreviewProvider>
                        {isMobile ? (
                            // Mobile layout - single component
                            <EmbeddedWorkflowPreviewMobile onAddMediaItems={onAddMediaItems}>
                                <FilePreview
                                    key={`file-${currentMedia?.file.id}`}
                                    forceHideLabels={!showLabels}
                                    strategy={BoundingBoxStrategyImpl}
                                />
                            </EmbeddedWorkflowPreviewMobile>
                        ) : (
                            // Desktop layout - component + integration code snippet
                            <>
                                <EmbeddedWorkflowPreview
                                    className="h-[80vh] min-h-[80vh] w-full flex-shrink-0"
                                    onAddMediaItems={onAddMediaItems}
                                    uploadLimitError={uploadLimitError}
                                    isHovered={isHovered}
                                >
                                    <FilePreview
                                        key={`file-${currentMedia?.file.id}`}
                                        forceHideLabels={!showLabels}
                                        strategy={BoundingBoxStrategyImpl}
                                    />
                                </EmbeddedWorkflowPreview>
                                {copy && (
                                    <IntegrationCodeSnippet
                                        copy={copy}
                                        blurred={!user}
                                        isExpanded={false}
                                    />
                                )}
                            </>
                        )}
                    </FilePreviewProvider>
                </div>

                {/* Video Trimmer Modal */}
                {mediaItemToTrim?.validation?.needsTrimming === true && <VideoTrimmer />}

                {/* Out of Credits Modal */}
                <OutOfCreditsModal isOpen={isOutOfCredits} onClose={clearOutOfCreditsError} />
            </div>
        </AnimatePresence>
    );
}
